import type { ButtonProps } from '@sravni/react-design-system/lib/Button';
import { useBoolean } from '@sravni/react-utils';
import { useMemo, useState } from 'react';

import type { MPLPreApprovalOffer } from 'commonTypes/api/scenarios/CreditSelectionScreenContent';

import { sendSentryClientErrorOnce, useAppDispatch, useAppSelector } from 'shared/lib';
import type { TOffer } from 'shared/types/TRankingService';

import { collectProductRequestPayloadThunk, selectRegionLocationData } from 'entities/creditForm';
import { currentLocationSelector } from 'entities/locations';
import { createOfferNoteDataFromOffer, offerNoteDataToView } from 'entities/offerNote';
import { OfferDetailsPopup } from 'entities/offerPopups/ui/OfferDetailsPopup/OfferDetailsPopup';
import { useLoadRankingOfferByMplOffer } from 'entities/scenario/lib';

import { getControlsByMplPreApprovalOffer } from '../../lib';
import { OfferButton } from '../OfferButton/OfferButton';

import { MORE_INFO_TEXTS } from './MoreInfo.texts';

interface IMoreInfoProps {
  offer: MPLPreApprovalOffer;
  buttonProps?: ButtonProps;
}

export const MplOfferMoreInfo = ({ offer, buttonProps }: IMoreInfoProps) => {
  const { loadOffer, isLoading } = useLoadRankingOfferByMplOffer();
  const dispatch = useAppDispatch();
  const [isPopupOpen, setIsPopupOpen] = useBoolean();
  const headerLocation = useAppSelector(currentLocationSelector);
  const regionLocation = useAppSelector(selectRegionLocationData);
  const [loadedOffer, setLoadedOffer] = useState<TOffer | null>(null);
  const noteData = loadedOffer ? createOfferNoteDataFromOffer(loadedOffer) : null;
  const note = offerNoteDataToView(noteData);

  const controls = useMemo(() => getControlsByMplPreApprovalOffer(offer), [offer]);

  const onClick = async () => {
    if (loadedOffer) {
      setIsPopupOpen.on();
    }
    try {
      const formData = dispatch(collectProductRequestPayloadThunk());
      const rankingOffer = await loadOffer(offer, formData);
      if (rankingOffer) {
        setLoadedOffer(rankingOffer);
        setIsPopupOpen.on();
      }
    } catch (error) {
      sendSentryClientErrorOnce('MplOfferMoreInfo click error', error);
    }
  };

  return (
    <>
      <OfferButton
        {...buttonProps}
        loading={buttonProps?.loading || isLoading}
        variant="secondary"
        color="dark"
        onClick={onClick}
      >
        {MORE_INFO_TEXTS.LABEL}
      </OfferButton>

      {isPopupOpen && loadedOffer && (
        <OfferDetailsPopup
          offer={loadedOffer}
          onClose={setIsPopupOpen.off}
          location={regionLocation || headerLocation}
          note={note}
          controls={controls}
        />
      )}
    </>
  );
};
